import {omit} from 'es-toolkit'
import {intersection, isInteger} from 'es-toolkit/compat'

import {MinMaxRange} from '../types'

export interface StringMatchingFilterParams<TableRow> {
  key: keyof TableRow
  filterValues: string[]
}

export interface BooleanMatchingFilterParams<TableRow> {
  key: keyof TableRow
  filterValue: boolean | undefined
}

export interface ToggleMatchingFilterParams<TableRow> {
  key: keyof TableRow
  filterValue: boolean
}

export interface RangeMatchingFilterParams<TableRow> {
  minKey: keyof TableRow
  maxKey: keyof TableRow
  range: MinMaxRange
}

export type FilterParams<TableRow> =
  | StringMatchingFilterParams<TableRow>
  | BooleanMatchingFilterParams<TableRow>
  | RangeMatchingFilterParams<TableRow>

export abstract class Filter<TableRow, Params> {
  protected params: Params

  constructor(params: Params) {
    this.params = params
  }

  serialize() {
    return this.params
  }

  abstract isActive(): boolean

  abstract getLabel(label: string): string

  abstract createCopy(newFilterValue: unknown): Filter<TableRow, Params>

  abstract filterTableRows(rows: TableRow[]): TableRow[]
}

export class StringMatchingFilter<TableRow> extends Filter<
  TableRow,
  StringMatchingFilterParams<TableRow>
> {
  isActive() {
    return this.params.filterValues.length > 0
  }

  getLabel(label: string) {
    return this.isActive() ? `${label} (${this.params.filterValues.length})` : label
  }

  createCopy(newFilterValue: string[]): StringMatchingFilter<TableRow> {
    return new StringMatchingFilter({
      ...this.serialize(),
      filterValues: newFilterValue,
    })
  }

  filterTableRows(rows: TableRow[]) {
    if (this.params.filterValues.length === 0) {
      return rows
    }

    return rows.filter((row: TableRow) => {
      const value = row[this.params.key]
      if (typeof value === 'string') {
        return this.params.filterValues.includes(value)
      }
      if (Array.isArray(value)) {
        return intersection(this.params.filterValues, value).length > 0
      }
      return false
    })
  }
}

export class BooleanMatchingFilter<TableRow> extends Filter<
  TableRow,
  BooleanMatchingFilterParams<TableRow>
> {
  isActive() {
    return this.params.filterValue !== undefined
  }

  getLabel(label: string) {
    return label
  }

  createCopy(newFilterValue: boolean): BooleanMatchingFilter<TableRow> {
    return new BooleanMatchingFilter({
      ...this.serialize(),
      filterValue: newFilterValue,
    })
  }

  filterTableRows(rows: TableRow[]) {
    if (!this.isActive()) {
      return rows
    }

    return rows.filter((row: TableRow) => {
      const value = row[this.params.key]
      return this.params.filterValue === !!value
    })
  }
}

export class ToggleMatchingFilter<TableRow> extends Filter<
  TableRow,
  ToggleMatchingFilterParams<TableRow>
> {
  isActive(): boolean {
    return this.params.filterValue === true
  }

  getLabel(label: string): string {
    return label
  }

  createCopy(newFilterValue: boolean): Filter<TableRow, ToggleMatchingFilterParams<TableRow>> {
    return new ToggleMatchingFilter({
      ...this.serialize(),
      filterValue: newFilterValue,
    })
  }

  filterTableRows(rows: TableRow[]): TableRow[] {
    // Only filter rows for value "true"
    if (!this.isActive()) {
      return rows
    }

    return rows.filter((row: TableRow) => {
      const value = row[this.params.key]
      return this.params.filterValue === !!value
    })
  }
}

export class RangeMatchingFilter<TableRow> extends Filter<
  TableRow,
  RangeMatchingFilterParams<TableRow>
> {
  isActive() {
    return isInteger(this.params.range.min) || isInteger(this.params.range.max)
  }

  getLabel(label: string) {
    return label
  }

  createCopy(newFilterValue: MinMaxRange): RangeMatchingFilter<TableRow> {
    return new RangeMatchingFilter({
      ...this.serialize(),
      range: newFilterValue,
    })
  }

  filterTableRows(rows: TableRow[]) {
    return rows.filter(row => {
      const filterRange = this.params.range
      if (!filterRange.min && !filterRange.max) {
        return true
      }
      const min = row[this.params.minKey]
      const max = row[this.params.maxKey]

      if (typeof min !== 'number' || typeof max !== 'number') {
        return false
      }

      const matchingMin = filterRange.min ? min >= filterRange.min : true
      const matchingMax = filterRange.max ? max <= filterRange.max : true
      return matchingMin && matchingMax
    })
  }
}

export const filterTableRows = <TableRow>(
  tableRows: TableRow[],
  filters: Filter<TableRow, unknown>[]
): TableRow[] => filters.reduce((acc, filter) => filter.filterTableRows(acc), tableRows)

export const getCount = <TableRow>(
  tableRows: TableRow[],
  filterMap: Record<string, Filter<TableRow, FilterParams<TableRow>>>,
  filterKey: string,
  newFilter: Filter<TableRow, FilterParams<TableRow>>
) => {
  const partialFilters = Object.values(omit(filterMap, [filterKey])) as Filter<
    TableRow,
    FilterParams<TableRow>
  >[]
  const newFilters = [...partialFilters, newFilter]
  return filterTableRows(tableRows, newFilters).length
}
